import { subDays, subYears } from 'date-fns';
import { CalendarDay } from 'further-ui/utils';
import { IncomeStatementDateOption } from 'further-types/lp/general-ledger';

export default function getColumnHeaders(
  dateOption: IncomeStatementDateOption,
  periodStartDate?: CalendarDay,
  fyStartDate?: CalendarDay,
  periodEndDate?: CalendarDay,
  inceptionDate?: CalendarDay,
) {
  const period1Start = periodStartDate?.toReadable() ?? '';
  const period1End = periodEndDate?.toReadable() ?? '';
  const fyStart = fyStartDate?.toReadable() ?? '';
  const inception = inceptionDate?.toReadable() ?? '';

  switch (dateOption) {
    case IncomeStatementDateOption.CurrentQuarter:
      return [
        `Current quarter\n(${period1Start} - ${period1End})`,
        `Year-to-date\n(${fyStart} - ${period1End})`,
        `Since inception\n(${inception} - ${period1End})`,
      ];
    case IncomeStatementDateOption.LastQuarter:
      return [
        `Last quarter\n(${period1Start} - ${period1End})`,
        `Year-to-date\n(${fyStart} - ${period1End})`,
        `Since inception\n(${inception} - ${period1End})`,
      ];
    case IncomeStatementDateOption.FinancialYearToDate: {
      // For previous FY, calculate the year before using date-fns
      const prevFyStartDate = fyStartDate
        ? subYears(fyStartDate.toUTCDate(), 1)
        : null;
      const prevFyStart = prevFyStartDate
        ? new CalendarDay(prevFyStartDate).toReadable()
        : '';

      const prevFyEndDate = periodStartDate
        ? subDays(periodStartDate.toUTCDate(), 1)
        : null;
      const prevFyEnd = prevFyEndDate
        ? new CalendarDay(prevFyEndDate).toReadable()
        : '';

      return [
        `Financial year-to-date\n(${fyStart} - ${period1End})`,
        `Previous financial year\n(${prevFyStart} - ${prevFyEnd})`,
        `Since inception\n(${inception} - ${period1End})`,
      ];
    }
    case IncomeStatementDateOption.LastFinancialYear: {
      // For last financial year, calculate the previous year using date-fns
      const prevYearStartDate = periodStartDate
        ? subYears(periodStartDate.toUTCDate(), 1)
        : null;
      const prevYearStart = prevYearStartDate
        ? new CalendarDay(prevYearStartDate).toReadable()
        : '';

      const prevYearEndDate = periodStartDate
        ? subDays(periodStartDate.toUTCDate(), 1)
        : null;
      const prevYearEnd = prevYearEndDate
        ? new CalendarDay(prevYearEndDate).toReadable()
        : '';

      return [
        `Last financial year\n(${period1Start} - ${period1End})`,
        `Previous year\n(${prevYearStart} - ${prevYearEnd})`,
        `Since inception\n(${inception} - ${period1End})`,
      ];
    }
    case IncomeStatementDateOption.Custom: {
      // For custom date, Period 1 is from some start point to the custom date
      // Period 2 is YTD from FY start to the custom date
      // Period 3 is since inception to the custom date
      const customStartDate = periodStartDate?.toReadable() ?? '';
      const customEndDate = periodEndDate?.toReadable() ?? '';
      return [
        `Selected period\n(${customStartDate} - ${customEndDate})`,
        `Year-to-date\n(${fyStart} - ${customEndDate})`,
        `Since inception\n(${inception} - ${customEndDate})`,
      ];
    }
    default:
      return ['Period 1', 'Period 2', 'Period 3'];
  }
}
